import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { fetchEntityBasic } from '../../services/deepLinkService';

const CACHE_TTL_MS = 60 * 1000;
const CACHE_MAX_ENTRIES = 20;
const RETRY_BASE_DELAY_MS = 500;
const MAX_ATTEMPTS = 3;

const memoryCache = new Map();

// Assumes numeric entityId (all current resolvers int.TryParse their IDs before
// returning). If a future resolver uses alphanumeric ids, align with the URL
// encoding in deeplink_gate_entity so the same entity cannot land in two entries.
const cacheKey = (entityType, entityId) =>
    `${(entityType ?? '').toLowerCase()}|${entityId ?? ''}`;

const cacheGet = (key) => {
    const entry = memoryCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        memoryCache.delete(key);
        return null;
    }
    memoryCache.delete(key);
    memoryCache.set(key, entry);
    return entry.value;
};

const cacheSet = (key, value) => {
    if (memoryCache.size >= CACHE_MAX_ENTRIES) {
        const oldestKey = memoryCache.keys().next().value;
        memoryCache.delete(oldestKey);
    }
    memoryCache.set(key, { value, timestamp: Date.now() });
};

export const invalidateDeepLinkCache = () => {
    memoryCache.clear();
};

const isCancelError = (error) =>
    axios.isCancel(error) || error?.name === 'CanceledError' || error?.name === 'AbortError';

const isRetriableError = (error) => {
    if (!error) return false;
    if (isCancelError(error)) return false;
    if (!error.response) return true; // network/timeout
    const status = error.response.status;
    return status === 500 || status === 502 || status === 503 || status === 504;
};

/**
 * Fetches basic info for a given entity from the deep-link gate endpoint.
 * Behaviour:
 *   - Aborts any in-flight request when entityType or entityId changes (prevents race conditions).
 *   - Retries transient failures (network + 5xx) with exponential backoff, up to MAX_ATTEMPTS.
 *   - Caches successful responses for CACHE_TTL_MS (LRU, CACHE_MAX_ENTRIES).
 *   - Sets _suppressErrorPopup on the axios config so the global interceptor does not show SweetAlert2.
 */
const useDeepLinkFetch = (entityType, entityId, resolver) => {
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const abortRef = useRef(null);

    useEffect(() => {
        if (!resolver || !entityType || !entityId) {
            setState({ loading: false, error: { code: 'unsupported' }, data: null });
            return undefined;
        }

        const key = cacheKey(entityType, entityId);
        const cached = cacheGet(key);
        if (cached) {
            setState({ loading: false, error: null, data: cached });
            return undefined;
        }

        if (abortRef.current) {
            abortRef.current.abort();
        }
        const controller = new AbortController();
        abortRef.current = controller;
        let retryTimer = null;

        const attempt = (attemptsLeft, delay) => {
            setState((prev) => ({ ...prev, loading: true, error: null }));

            fetchEntityBasic(resolver.fetchPath, entityId, { signal: controller.signal })
                .then((response) => {
                    const payload = response?.data?.data ?? response?.data ?? null;
                    if (!payload) {
                        setState({ loading: false, error: { code: 'notFound' }, data: null });
                        return;
                    }
                    cacheSet(key, payload);
                    setState({ loading: false, error: null, data: payload });
                })
                .catch((error) => {
                    if (isCancelError(error)) {
                        return;
                    }

                    if (isRetriableError(error) && attemptsLeft > 0) {
                        retryTimer = setTimeout(() => {
                            retryTimer = null;
                            if (!controller.signal.aborted) {
                                attempt(attemptsLeft - 1, delay * 2);
                            }
                        }, delay);
                        return;
                    }

                    const status = error?.response?.status;
                    let code = 'error';
                    if (status === 404) code = 'notFound';
                    else if (status === 403) code = 'forbidden';
                    setState({ loading: false, error: { code, status }, data: null });
                });
        };

        attempt(MAX_ATTEMPTS - 1, RETRY_BASE_DELAY_MS);

        return () => {
            controller.abort();
            if (retryTimer) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
        };
        // `resolver` MUST be a referentially stable object (module-level registry
        // from entityResolvers.js). Passing an inline literal each render would
        // re-fire this effect and abort in-flight requests on every parent render.
    }, [entityType, entityId, resolver]);

    return state;
};

export default useDeepLinkFetch;
